<template>
  <div class="not-found">
    <div class="not-found-content">
      <h2 v-if="pageInfo.code" class="code">{{ pageInfo.code }}</h2>
      <h3 class="title" :class="{ 'no-code': !pageInfo.code }">{{ pageInfo.title }}</h3>
      <h4 class="desc">{{ pageInfo.desc }}</h4>

      <div class="btn-box">
        <el-button
          v-for="item in ERROR_BTN_LIST"
          :key="item.btn_name"
          class="eyeglasses"
          type="primary"
          @click="routerPush(item.url, item.btn_name)"
          >{{ item.btn_name }}</el-button
        >
      </div>
    </div>
    <div class="center">
      <div v-for="item in CENTER_INFO" :key="item.title" class="item">
        <NuxtImgWrap :src="item.img" alt="vooglam glasses" class="item-left" />
        <div class="item-right">
          <p class="item-title">{{ item.title }}</p>
          <p class="item-desc">{{ item.desc() }}</p>
        </div>
      </div>
    </div>
    <cartRecommendList
      :groupId="6"
      :isShowHalfLabel="false"
      :showUnderlinePrice="true"
      :inErrorPage="inErrorPage"
      :sdYouAlsoLikeMktExp="sdYouAlsoLikeMktExp"
      :sdYouAlsoLikeMktClick="sdYouAlsoLikeMktClick"
    />
  </div>
</template>

<script lang="ts" setup>
import type { IPriceInfo } from '@/api/types/currency-price';
import day30 from '@/assets/images/404/30_day.png';
import day365 from '@/assets/images/404/365_day.png';
import FreeShipping from '@/assets/images/404/free_shipping.png';
import cartRecommendList from '@/components/public/might-like/cartRecommendList.vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useStore } from '~/store';
import { useNotFoundSensors } from './composables/useNotFoundSensors';
import { ERROR_BTN_LIST, ERROR_PAGE_INFO } from './constants';

const store = useStore();
const { currency } = storeToRefs(store);
const { getPublicResources, getPublicConfig, getModelListTemplate } = store;

const router = useRouter();

interface Props {
  pageType: string; // 页面类型 '404' '410' 'activity' (营销活动结束)
  inErrorPage?: boolean;
  market_activity_name?: string;
}
const props = withDefaults(defineProps<Props>(), {
  inErrorPage: false,
  market_activity_name: '',
});

const usFreeShippingList = ref<IPriceInfo[]>([]);
const freePrice = computed(() => {
  const _priceInfo = usFreeShippingList.value.find((item) => item.currency === currency.value.name);
  return _priceInfo?.price || '';
});
const pageInfo = computed(() => {
  return ERROR_PAGE_INFO[props.pageType] || {};
});
const pageName = computed(() => {
  return props.pageType === 'activity'
    ? `营销活动页面_${props.market_activity_name}`
    : `${props.pageType}页面`;
});
const { SD_AbnormalPageView, SD_AbnormalPageBtnClick, SD_AbnormalPageValidProductExp } =
  useNotFoundSensors();

interface CenterInfoItem {
  /** 图片资源 URL（Vite 静态导入） */
  img: string;
  title: string;
  desc: () => string;
}

const CENTER_INFO: CenterInfoItem[] = [
  {
    img: FreeShipping,
    title: 'FREE SHIPPING',
    desc: () => {
      return freePrice.value
        ? `Over ${currency.value.symbol_prefix}${freePrice.value}${currency.value.symbol_suffix}`
        : '';
    },
  },
  {
    img: day30,
    title: '30-DAY',
    desc: () => 'Return Warranty',
  },
  {
    img: day365,
    title: '365-DAY',
    desc: () => 'Quality Warranty',
  },
];

const routerPush = (url: string, btn_name: string) => {
  router.push(url);
  SD_AbnormalPageBtnClick(pageName.value, btn_name);
};

onMounted(async () => {
  const res = await getPublicResources({ info: ['us_free_shipping'] });
  const _list = res?.info?.us_free_shipping;
  if (Array.isArray(_list)) {
    usFreeShippingList.value = res.info.us_free_shipping;
  }

  getPublicConfig();
  getModelListTemplate();
  SD_AbnormalPageView(pageName.value);
});

function sdYouAlsoLikeMktExp(product: Record<string, any>) {
  SD_AbnormalPageValidProductExp(
    'AbnormalPageValidProduct_Exp',
    pageName.value,
    product.sensorData || {},
  );
}
function sdYouAlsoLikeMktClick(product: Record<string, any>) {
  SD_AbnormalPageValidProductExp(
    'AbnormalPageProduct_Click',
    pageName.value,
    product.sensorData || {},
  );
}
</script>

<style lang="scss" scoped>
.not-found {
  position: relative;
  .not-found-content {
    @include flex(normal, center, $dir: column);

    padding-top: 40px;
    font-family: $f-pr;
    background: #f7f5f2;
    .code {
      line-height: 130px;
      font-size: 110px;
      font-weight: bold;
    }
    .title {
      font-size: 28px;
      font-weight: bold;
      &.no-code {
        padding-top: 24px;
      }
    }
    .desc {
      margin-top: 6px;
      font-size: 14px;
      font-weight: normal;
    }
    .btn-box {
      @include flex;

      margin-top: 38px;
      padding-bottom: 64px;
      gap: 40px;
      .el-button {
        margin-left: 0;
        width: 234px;
        height: 48px;
        border: none;
        border-radius: 24px;
        font-size: 16px;
        font-family: $f-pb;
        color: white;
        background-color: #080506;
      }
    }
  }
  .center {
    @include flex(center, center);

    margin-top: 40px;
    margin-bottom: -20px;
    height: 48px;
    gap: 200px;
    .item {
      @include flex(normal, center);
      .item-left {
        margin-right: 12px;
        width: 48px;
        height: 48px;
      }
      .item-right {
        font-size: 16px;
        font-family: $f-lr;
        .item-title {
          line-height: 18px;
        }
        .item-desc {
          margin-top: 3px;
          line-height: 20px;
          color: #5a544e;
        }
      }
    }
  }
}
</style>
